import type { SyncTransport } from './sync-transport';
import type { SyncEngine } from './sync-engine';
import { useEntityStore, type OpResult, type PendingOp, type PendingSnapshotOp } from './entity-store';
import { bytesToUuid } from './envelope';
import { CorrelationError } from './correlation-error';
import { message } from '@nula/core-ui';

const CORRELATION_TIMEOUT_MS = 30_000;

interface PendingBatch {
  /** The opId (persistent key) of the op in this batch. */
  opId: string;
  /** When the batch was sent. */
  sentAt: number;
}

interface EphemeralBatch {
  resolve: (results: OpResult[]) => void;
  reject: (err: CorrelationError) => void;
  timeoutHandle: ReturnType<typeof setTimeout>;
}

export interface OpSyncHandle {
  /** Tear down watchers and clear in-memory state. */
  stop(): void;
  /**
   * Resolve a batch by envelope corrId. Tries the ephemeral map first
   * (one-off awaitable ops) then falls back to the persistent map
   * (pendingOps flushes). Stale corrIds (not in either map — e.g. response
   * after a reconnect cycle) are silently dropped.
   */
  resolveBatch(corrId: Uint8Array, results: OpResult[]): void;
  /** True iff the corrId corresponds to a batch currently in flight. */
  hasPendingBatch(corrId: string): boolean;
  /**
   * Send a one-off ops envelope and await the correlated OpResult. Bypasses
   * the persistent pendingOps queue in entityStore — use for read-style ops
   * that don't need reload survival. Resolves with the per-op results from
   * the OpResult envelope.
   */
  sendEphemeralBatch(op: PendingOp): Promise<OpResult[]>;
  /**
   * Snapshot-rail equivalent of `sendEphemeralBatch`. Used by snapshot
   * preview reads which submit `KIND_SNAPSHOT_OP` envelopes and await the
   * correlated OpResult. `binary` is optional — used by snapshot create with
   * a client-supplied Yjs state.
   */
  sendEphemeralSnapshotBatch(
    op: PendingSnapshotOp,
    options?: { binaryTarget?: string; binary?: Uint8Array },
  ): Promise<OpResult[]>;
}

/**
 * Bridge between entityStore (pending ops) and syncEngine (WS transport).
 * Watches pendingOps for new unsent ops and sends them when connected.
 * Replays all pending ops on reconnect (marks all unsent first).
 *
 * Dual-key tracking for persistent ops:
 * - opId: persistent key (lives in entityStore.pendingOps, survives reload)
 * - corrId: ephemeral key (generated at send time, cleared on disconnect)
 *
 * Ephemeral ops (sendEphemeralBatch) use only the corrId key and live in
 * the ephemeralMap — they never touch entityStore.pendingOps.
 *
 * Returns an OpSyncHandle that the dispatcher and services can call to
 * route OpResult envelopes and submit awaitable one-off ops.
 */
export function startOpSync(
  transport: SyncTransport,
  engine: SyncEngine,
  sendSync?: (after: string | null) => void,
): OpSyncHandle {
  // Persistent ops correlation map: corrId → PendingBatch.
  // Populated by sendUnsentOps, drained by resolveBatch, cleared on disconnect.
  const corrMap = new Map<string, PendingBatch>();

  // Ephemeral ops correlation map: corrId → EphemeralBatch.
  // Populated by sendEphemeralBatch, drained by resolveBatch's ephemeral branch,
  // rejected on disconnect so awaiting callers can surface the failure.
  const ephemeralMap = new Map<string, EphemeralBatch>();

  function sendUnsentOps(): void {
    if (transport.getStatus() !== 'connected') return;
    const state = useEntityStore.getState();
    for (const op of state.pendingOps) {
      if (!op.sent) {
        const { corrId, sent } = engine.sendOps(op);
        if (sent) {
          corrMap.set(corrId, { opId: op.opId, sentAt: Date.now() });
          state.markOpSent(op.opId);
        }
      }
    }
    for (const op of state.pendingSnapshotOps) {
      if (!op.sent) {
        const { corrId, sent } = engine.sendSnapshotOps(op);
        if (sent) {
          corrMap.set(corrId, { opId: op.opId, sentAt: Date.now() });
          state.markSnapshotOpSent(op.opId);
        }
      }
    }
  }

  function sendEphemeralBatch(op: PendingOp): Promise<OpResult[]> {
    return new Promise<OpResult[]>((resolve, reject) => {
      if (transport.getStatus() !== 'connected') {
        reject(new CorrelationError('connection_lost', undefined, 'transport disconnected'));
        return;
      }
      const { corrId, sent } = engine.sendOps(op);
      if (!sent) {
        reject(new CorrelationError('connection_lost', undefined, 'transport send failed'));
        return;
      }
      const timeoutHandle = setTimeout(() => {
        const pending = ephemeralMap.get(corrId);
        if (pending) {
          ephemeralMap.delete(corrId);
          pending.reject(new CorrelationError('timeout', undefined, `ephemeral ops ${corrId} timed out`));
        }
      }, CORRELATION_TIMEOUT_MS);
      ephemeralMap.set(corrId, { resolve, reject, timeoutHandle });
    });
  }

  function sendEphemeralSnapshotBatch(
    op: PendingSnapshotOp,
    options?: { binaryTarget?: string; binary?: Uint8Array },
  ): Promise<OpResult[]> {
    return new Promise<OpResult[]>((resolve, reject) => {
      if (transport.getStatus() !== 'connected') {
        reject(new CorrelationError('connection_lost', undefined, 'transport disconnected'));
        return;
      }
      const { corrId, sent } = engine.sendSnapshotOps(op, options);
      if (!sent) {
        reject(new CorrelationError('connection_lost', undefined, 'transport send failed'));
        return;
      }
      const timeoutHandle = setTimeout(() => {
        const pending = ephemeralMap.get(corrId);
        if (pending) {
          ephemeralMap.delete(corrId);
          pending.reject(
            new CorrelationError('timeout', undefined, `ephemeral snapshot-ops ${corrId} timed out`),
          );
        }
      }, CORRELATION_TIMEOUT_MS);
      ephemeralMap.set(corrId, { resolve, reject, timeoutHandle });
    });
  }

  function resolveBatch(corrId: Uint8Array, results: OpResult[]): void {
    const corrIdStr = bytesToUuid(corrId);

    // Ephemeral ops branch — awaitable one-off sends.
    const ephemeral = ephemeralMap.get(corrIdStr);
    if (ephemeral) {
      ephemeralMap.delete(corrIdStr);
      clearTimeout(ephemeral.timeoutHandle);
      ephemeral.resolve(results);
      return;
    }

    // Persistent ops branch — entityStore.pendingOps flush.
    const batch = corrMap.get(corrIdStr);
    if (!batch) {
      // Stale — e.g. the connection reset between send and receive. Persistent
      // ops remain in entityStore.pendingOps and will be replayed on reconnect
      // with fresh corrIds; the server's op_log dedup handles "already
      // processed" safely. Ephemeral ops that landed here without a match
      // were either already resolved or abandoned on disconnect.
      return;
    }
    corrMap.delete(corrIdStr);

    // Apply per-op results. A persistent batch maps to a single opId today,
    // but the wire protocol allows N sub-ops per opId. Per-op resolution is
    // load-bearing here: a coarse "any-denied → reject the whole batch"
    // policy used to surface "you don't have permission" toasts on multi-
    // delete flows where most sub-ops succeeded.
    const byOpId = new Map<string, OpResult[]>();
    for (const r of results) {
      const list = byOpId.get(r.opId) ?? [];
      list.push(r);
      byOpId.set(r.opId, list);
    }
    for (const [opId, opResults] of byOpId) {
      const allOk = opResults.every((r) => r.status === 'ok');
      const allDenied = opResults.length > 0 && opResults.every((r) => r.status === 'permissionDenied');
      const deniedResults = opResults.filter((r) => r.status === 'permissionDenied');
      const okCount = opResults.filter((r) => r.status === 'ok').length;

      if (allOk) {
        // Includes the idempotent-Delete case (server returns Ok with message
        // "not found — idempotent" for Delete on a missing row): no toast,
        // no bootstrap.
        useEntityStore.getState().confirmOp(opId, opResults);
        continue;
      }

      if (allDenied) {
        // Whole batch is server-rejected for permissions — surface the toast
        // and trigger a bootstrap to restore optimistically-stale state.
        useEntityStore.getState().rejectOp(opId);
        message.error("You don't have permission to do that");
        if (sendSync) sendSync(null);
        continue;
      }

      if (deniedResults.length > 0) {
        // MIXED: some sub-ops succeeded, at least one was permission-denied.
        // Apply version updates for the OK sub-ops, drop the pending op, and
        // reset the cursor so the next reconnect bootstrap restores the
        // optimistically-stale rows for the denied sub-ops. Toast aggregates
        // the rejection so the user sees ONE message rather than per-sub-op spam.
        useEntityStore.getState().partialConfirm(opId, opResults);
        const total = opResults.length;
        message.error(
          `Couldn't complete ${deniedResults.length} of ${total} item${total === 1 ? '' : 's'} — you don't have permission`,
        );
        if (sendSync) sendSync(null);
        continue;
      }

      // No permission denials, but not all-OK either — conflicts/errors mixed
      // with successes. Apply OK version updates, drop the pending op, and
      // bootstrap to reconcile any optimistic state for the failed sub-ops.
      // No toast: conflicts/errors are silent today (matches previous discard path).
      if (okCount > 0) {
        useEntityStore.getState().partialConfirm(opId, opResults);
      } else {
        useEntityStore.getState().discardOp(opId);
      }
    }
  }

  function clearEphemeralOnDisconnect(): void {
    const err = new CorrelationError('connection_lost', undefined, 'transport disconnected');
    for (const pending of ephemeralMap.values()) {
      clearTimeout(pending.timeoutHandle);
      pending.reject(err);
    }
    ephemeralMap.clear();
  }

  // Watch for new pending ops → send when connected.
  // entityStore uses plain persist middleware (no subscribeWithSelector),
  // so we use the two-argument form and compare manually.
  const unsubOps = useEntityStore.subscribe((state, prevState) => {
    if (
      state.pendingOps.length !== prevState.pendingOps.length ||
      state.pendingSnapshotOps.length !== prevState.pendingSnapshotOps.length
    ) {
      sendUnsentOps();
    }
  });

  // Send any pre-existing unsent ops immediately
  sendUnsentOps();

  // On reconnect → clear stale corr_ids, mark all unsent, then replay
  const unsubStatus = transport.onStatus((status) => {
    if (status === 'connected') {
      // Correlation map is only valid for a specific connection.
      // Clear it so stale responses from the previous connection are ignored.
      corrMap.clear();
      clearEphemeralOnDisconnect();
      useEntityStore.getState().markAllUnsent();
      sendUnsentOps();
    }
    if (status === 'disconnected') {
      // Connection lost — stale corr_ids are invalid.
      corrMap.clear();
      clearEphemeralOnDisconnect();
    }
  });

  return {
    stop() {
      unsubOps();
      unsubStatus();
      corrMap.clear();
      clearEphemeralOnDisconnect();
    },
    resolveBatch,
    hasPendingBatch(corrIdStr: string) {
      return corrMap.has(corrIdStr) || ephemeralMap.has(corrIdStr);
    },
    sendEphemeralBatch,
    sendEphemeralSnapshotBatch,
  };
}
